import { Box, Tooltip as MuiTooltip, Stack, TooltipProps, Typography, styled, tooltipClasses } from "@mui/material";
import { FC, ReactNode, useState, useRef, useEffect } from "react";

import { IconClose } from "@/modules/irohana-components/assets/icons";
import { IconOutlineInfo } from "@/modules/irohana-components/assets/icons/IconOutlineInfo";
import { IconBox } from "@/modules/irohana-components/elements/IconBox";
import { background, text } from "@/modules/irohana-components/theme/colors";
import { rounded, spacing } from "@/modules/irohana-components/theme/variables";

export type TTooltipProps = {
  title?: string;
  content?: ReactNode;
  width?: number;
  hasIcon?: boolean;
} & Omit<TooltipProps, "title" | "content">;

export const Tooltip: FC<TTooltipProps> = ({ title, content, placement = "top", width = 400, hasIcon = true, ...rest }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        triggerRef.current &&
        tooltipRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <StyledTooltip
      open={open}
      placement={placement}
      arrow
      title={
        <Stack gap={2} ref={tooltipRef}>
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {hasIcon && <IconOutlineInfo fontSize={24} />}
              <Typography variant="body14Bold">{title || "留意事項"}</Typography>
            </Stack>
            <IconBox onClick={handleToggle}>
              <IconClose fontSize={24} />
            </IconBox>
          </Stack>
          <Box width={width}>{content}</Box>
        </Stack>
      }
      {...rest}
    >
      <Stack
        ref={triggerRef}
        sx={{ cursor: "pointer", height: "fit-content" }}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
      >
        {rest.children}
      </Stack>
    </StyledTooltip>
  );
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
    backgroundColor: background.white,
    color: text.primary,
    padding: spacing.sm,
    borderRadius: rounded.sm,
    boxShadow: "0px 2px 8px 0px #00000040",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
    marginTop: "4px",
    marginLeft: "4px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: background.white,
  },
}));
